import { useEffect } from 'react'
import toast from 'react-hot-toast'
import { Navigate, useParams } from 'react-router'

import { useFvNavigate } from '@fv/client-components'
import type { FullShipment } from '@fv/client-types'

import { Loading } from '@/components/Loading'
import { ModeNav } from '@/components/ModeNav'
import { ShipmentTemplateSelector } from '@/features/load-templates/ShipmentTemplateSelector'
import { EquipmentList } from '@/features/quote-request/EquipmentList'
import { QuoteRequestFooter } from '@/features/quote-request/QuoteRequestFooter'
import { QuoteRequestLocations } from '@/features/quote-request/QuoteRequestLocations'
import { useQuoteRequestTemplate } from '@/hooks/shipments/useRepeatShipment'
import { useHomePage } from '@/route-hooks'
import { routes } from '@/routes'

import { HasFeature } from '../auth'
import { useWorkflowEnabled } from '../auth/HasWorkflow'
import { useDefaultWorkflow } from '../profiile/hooks'
import { useUnifiedShipmentFlow } from '../route-guides/hooks'
import { QuoteRequestAutofill } from './QuoteRequestAutofill'
import { QuoteRequestHandlingUnits } from './QuoteRequestHandlingUnits'
import type { QuoteFormParams } from './types'
import { useCanUseTemplates } from './useCanUseMultiTruck'
import {
  resetQuoteForm,
  useQuoteFormActions,
  useQuoteFormState,
} from './useQuoteFormState'
import { useQuoteRequest } from './useQuoteRequest'

export const QuoteRequestForm = () => {
  const { applyLoadTemplate, clearLoadTemplate, initializeFromTemplate } =
    useQuoteFormActions()
  const selectedTemplate = useQuoteFormState(s => s.selectedTemplate)
  const canUseTemplates = useCanUseTemplates()
  const homePage = useHomePage()
  const { mode, quoteRequestId } = useParams<QuoteFormParams>()
  const isEditing = !!quoteRequestId && mode === 'edit'
  const navigate = useFvNavigate()
  const requestQuery = useQuoteRequest(quoteRequestId, isEditing)
  const workflowEnabled = useWorkflowEnabled('truckload')
  const defaultWorkflow = useDefaultWorkflow()
  const unifiedFlow = useUnifiedShipmentFlow()
  const [copyTemplate] = useQuoteRequestTemplate()

  // Reset state on unmount
  useEffect(() => resetQuoteForm, [])

  // Default form values from template
  useEffect(() => {
    if (!quoteRequestId || !mode) return
    let template: FullShipment[] = []

    if (mode === 'copy') {
      template = [...(copyTemplate ?? [])]
    }

    if (mode === 'edit' && requestQuery.data) {
      template = requestQuery.data.filter(load => load.status === 'pending')
    }

    const templateRequestId = template[0]?.quoteRequestId

    // Mismatching ids or no pending loads on quote request
    if (templateRequestId !== quoteRequestId) {
      if (!requestQuery.isLoading) {
        toast.error('Quote request not found.')
        navigate('/', { replace: true })
      }

      return
    }

    initializeFromTemplate(template, mode)
  }, [
    initializeFromTemplate,
    mode,
    navigate,
    quoteRequestId,
    requestQuery.data,
    requestQuery.isLoading,
    copyTemplate,
  ])

  if (unifiedFlow.isLoading) return <Loading />

  if (unifiedFlow.isEnabled('truckload')) {
    if (isEditing) {
      if (requestQuery.isLoading) return <Loading />
      const editLoadId = requestQuery.data?.find(
        l => l.status === 'pending',
      )?.loadId
      // Without a pending shipment, the template effect above sends the user home.
      return editLoadId ? (
        <Navigate to={routes.editLoad(editLoadId)} replace />
      ) : (
        <Loading />
      )
    }

    const copyFrom = mode === 'copy' ? copyTemplate?.[0]?.loadId : undefined
    return (
      <Navigate
        to={copyFrom ? routes.copyLoad(copyFrom) : routes.create()}
        replace
      />
    )
  }

  if (!workflowEnabled) {
    return <Navigate to={routes.quote(defaultWorkflow)} replace />
  }

  return (
    <HasFeature name="truckLoad" redirect={homePage}>
      <ModeNav className="min-h-0">
        {!mode && (
          <ShipmentTemplateSelector
            selectedTemplate={selectedTemplate}
            disabled={!canUseTemplates}
            workflow="truckload"
            onSelect={applyLoadTemplate}
            onClear={clearLoadTemplate}
          />
        )}
      </ModeNav>
      <EquipmentList isEditing={isEditing} />
      <div className="py-8 px-6 bg-fv-gray-100 overflow-auto b1350:row-start-3">
        <QuoteRequestAutofill />
        <QuoteRequestLocations isEditing={isEditing} />
        <QuoteRequestHandlingUnits />
      </div>
      <QuoteRequestFooter
        isEditing={isEditing}
        mode={mode}
        quoteRequestId={quoteRequestId}
      />
    </HasFeature>
  )
}
